import type { MouseEvent } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import './PlaceCard.css';
import { useDispatch, useSelector } from 'react-redux';
import { addSavedPlaces, deleteSavedPlace } from '../store/savedPlacesSlice';
import type { AppDispatch } from '../store/store';
import { RootState } from '../store/store';
import useGoogleSignIn from '../hooks/useGoogleSignIn';

interface PlaceCardProps {
    id: string;
    name: string;
    imageUrl: string | null;
    city?: string | null;
    country?: string | null;
    saved: boolean;
    isSelected?: boolean;
    onHover?: (placeId: string | null) => void;
    onSelect?: (placeId: string | null) => void;
    onSavedToggle?: (placeId: string) => void;
    className?: string;
}

export default function PlaceCard(props: PlaceCardProps){
    const {
        id,
        name,
        imageUrl,
        city,
        country,
        saved,
        isSelected,
        onHover,
        onSelect,
        onSavedToggle,
        className,
    } = props;
    const userDetails = useSelector((state: RootState) => state.userDetails);
    const dispatch = useDispatch<AppDispatch>();
    const safeCity = city ?? null;
    const safeCountry = country ?? null;
    const locationLabel = [safeCity, safeCountry].filter(Boolean).join(', ');

    const onSignInSuccess = () => {
        dispatch(addSavedPlaces({id, name, city: safeCity, country: safeCountry}));
        onSavedToggle?.(id);
    };
    const { initiateGoogleSignIn } = useGoogleSignIn(onSignInSuccess);

    const onClickSaveIcon =  (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if(userDetails.userId){
            if (!saved) {
                dispatch(addSavedPlaces({id, name, city: safeCity, country: safeCountry}))
            }else{
                dispatch(deleteSavedPlace(id));
            }
            onSavedToggle?.(id);
        }else{
            initiateGoogleSignIn();
        }
    };

    const cardClasses = ['place-card-root'];
    if (isSelected) {
        cardClasses.push('place-card-root--selected');
    }

    return (
        <Card
            className={[...cardClasses, className].filter(Boolean).join(' ')}
            sx={{
                width: '100%',
                backgroundColor: 'transparent',
                boxShadow: 'none',
            }}
            elevation={0}
            onMouseEnter={() => onHover?.(id)}
            onMouseLeave={() => onHover?.(null)}
            onFocus={() => onHover?.(id)}
            onBlur={() => onHover?.(null)}
            onClick={() => onSelect?.(id)}
            tabIndex={0}
            role="button"
            aria-pressed={!!isSelected}
        >
            <div className='place-card-imageWrapper'>
            {imageUrl ? (
                <CardMedia
                    component="img"
                    alt={name}
                    image={imageUrl}
                    loading="lazy"
                    className='place-card-media'
                />
            ) : (
                <Skeleton 
                    variant="rectangular"
                    className='place-card-media place-card-media--skeleton'
                />
            )}
             <CardActions className='place-card-bookmark'>
                <IconButton 
                    aria-label="add to favorites" 
                    onClick={onClickSaveIcon}
                    className='place-card-bookmarkButton'
                >
                    {saved ? <BookmarkIcon fontSize='small' /> :  <BookmarkBorderIcon fontSize='small' /> }
                </IconButton>
             </CardActions>
             </div>
            <CardContent className='place-card-content'>
                <Typography variant="subtitle1" component="p" className='place-card-title'>
                    {name}
                </Typography>
                {locationLabel ? (
                    <Typography variant="body2" className='place-card-subtitle'>
                        {locationLabel}
                    </Typography>
                ) : (
                    <Typography variant="body2" className='place-card-placeholder'>
                        Location coming soon
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}
